import { appMessage } from '~/plugins/app_messages';

const FETCH_ERROR_STATUSES_WITHOUT_TOAST = [401, 403];

function notifyFetchError(error) {
  if (FETCH_ERROR_STATUSES_WITHOUT_TOAST.includes(error?.status)) {
    return;
  }

  appMessage(error?.responseJSON?.alert ?? 'An error has occurred.');
}

/**
 * Adapts PaginatedSelect JSON (AppLazySelect fetchers) to ZoneSelect's server `fetcher`.
 * Pass `server-all-ids` to the ZoneSelect: the fetcher answers `allIds` queries.
 *
 * @param {Object} config
 * @param {(payload: Record<string, unknown>) => Promise<{
 *   items?: Array<{ id: number, name: string, avatar?: string, disabled?: boolean, removed?: boolean }>,
 *   pagination?: { last_page?: boolean, next_page?: number | null },
 *   disabled_ids?: number[],
 *   selected_disabled_ids?: number[],
 *   selected_ids?: number[],
 * }>} config.request
 * @param {(item: object) => Record<string, unknown>} [config.mapOption]
 * @param {number} [config.pageSize=50]
 * @param {string} [config.selectedDisabledTooltip] Hover text for options disabled by `selected_disabled_ids`.
 * @returns {Function} List `fetcher` for ZoneSelect.
 */
export function createPaginatedSelectZoneFetcher({
  request,
  mapOption,
  pageSize = 50,
  selectedDisabledTooltip,
}) {
  // The server orders selected rows first. Later pages reuse page 1's selection so rows ticked
  // meanwhile don't reshuffle the order (skipping or repeating rows); page 1 is re-requested on
  // open and on every search, which refreshes it.
  let pageOneSelection = [];

  const mapResponseToOptions = (response) => {
    const selectedDisabledIds = new Set(response.selected_disabled_ids || []);
    const disabledIds = new Set([...(response.disabled_ids || []), ...selectedDisabledIds]);

    return (response.items || []).map((item) => {
      const base = {
        id: item.id,
        name: item.name,
        avatar: item.avatar,
        disabled: Boolean(item.disabled) || disabledIds.has(item.id),
        ...(selectedDisabledTooltip && selectedDisabledIds.has(item.id) ? { tooltip: selectedDisabledTooltip } : {}),
      };

      if (mapOption) {
        return { ...base, ...mapOption(item) };
      }

      return base;
    });
  };

  const listSelection = (state) => {
    // Label hydration (`selectedOnly`) must not overwrite the open picker's snapshot.
    // Several dialogs share one fetcher, so a closed sibling's hydrate would otherwise
    // reshuffle page 2 of the open list.
    if (state.selectedOnly) {
      return state.selected;
    }

    if (state.page === 1) {
      pageOneSelection = state.selected;
    }

    return pageOneSelection;
  };

  return async (state) => {
    const allIdsQuery = Boolean(state.allIds);
    const search = state.search || '';
    const selectionType = allIdsQuery
      ? (search ? 'select_all_matching' : 'select_all')
      : 'normal';

    let response;

    try {
      response = await request({
        search,
        page: allIdsQuery ? 1 : state.page,
        selected_only: state.selectedOnly,
        selection: allIdsQuery ? [] : listSelection(state),
        selection_type: selectionType,
      });
    } catch (error) {
      notifyFetchError(error);
      throw error;
    }

    const options = mapResponseToOptions(response);

    if (allIdsQuery) {
      const disabledIds = new Set([
        ...(response.disabled_ids || []),
        ...(response.selected_disabled_ids || []),
      ]);
      const rawIds =
        response.selected_ids?.length > 0 ? response.selected_ids : options.map((option) => option.id);
      const allIds = rawIds.filter((id) => !disabledIds.has(id));

      return { options, allIds };
    }

    // Match AppLazySelect: Kaminari sets next_page to null on the last page (including empty results).
    const hasMore = response.pagination?.next_page != null;

    return {
      options,
      hasMore,
    };
  };
}
